/**
 * Domain Error Types
 * Defines all types of API errors that can occur
 */

/**
 * Base API Error class
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public code?: string,
    public originalError?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

/**
 * Network Error - No response received from server
 */
export class NetworkError extends ApiError {
  constructor(message: string = 'Network error - no response received', originalError?: unknown) {
    super(message, undefined, 'NETWORK_ERROR', originalError);
    this.name = 'NetworkError';
    Object.setPrototypeOf(this, NetworkError.prototype);
  }
}

/**
 * Timeout Error - Request timed out
 */
export class TimeoutError extends ApiError {
  constructor(message: string = 'Request timeout', originalError?: unknown) {
    super(message, undefined, 'TIMEOUT_ERROR', originalError);
    this.name = 'TimeoutError';
    Object.setPrototypeOf(this, TimeoutError.prototype);
  }
}

/**
 * Client Error (4xx) - Client made an invalid request
 */
export class ClientError extends ApiError {
  constructor(
    message: string,
    statusCode: number,
    public details?: unknown,
    originalError?: unknown
  ) {
    super(message, statusCode, `CLIENT_ERROR_${statusCode}`, originalError);
    this.name = 'ClientError';
    Object.setPrototypeOf(this, ClientError.prototype);
  }
}

/**
 * Unauthorized Error (401)
 */
export class UnauthorizedError extends ClientError {
  constructor(message: string = 'Unauthorized access', details?: unknown, originalError?: unknown) {
    super(message, 401, details, originalError);
    this.name = 'UnauthorizedError';
    Object.setPrototypeOf(this, UnauthorizedError.prototype);
  }
}

/**
 * Forbidden Error (403)
 */
export class ForbiddenError extends ClientError {
  constructor(message: string = 'Forbidden access', details?: unknown, originalError?: unknown) {
    super(message, 403, details, originalError);
    this.name = 'ForbiddenError';
    Object.setPrototypeOf(this, ForbiddenError.prototype);
  }
}

/**
 * NotFound Error (404)
 */
export class NotFoundError extends ClientError {
  constructor(message: string = 'Resource not found', details?: unknown, originalError?: unknown) {
    super(message, 404, details, originalError);
    this.name = 'NotFoundError';
    Object.setPrototypeOf(this, NotFoundError.prototype);
  }
}

/**
 * Validation Error (422)
 */
export class ValidationError extends ClientError {
  constructor(
    message: string = 'Validation error',
    public validationErrors?: Record<string, string[]>,
    originalError?: unknown
  ) {
    super(message, 422, validationErrors, originalError);
    this.name = 'ValidationError';
    Object.setPrototypeOf(this, ValidationError.prototype);
  }
}

/**
 * Server Error (5xx) - Server encountered an error
 */
export class ServerError extends ApiError {
  constructor(
    message: string,
    statusCode: number,
    public details?: unknown,
    originalError?: unknown
  ) {
    super(message, statusCode, `SERVER_ERROR_${statusCode}`, originalError);
    this.name = 'ServerError';
    Object.setPrototypeOf(this, ServerError.prototype);
  }
}

/**
 * Internal Server Error (500)
 */
export class InternalServerError extends ServerError {
  constructor(message: string = 'Internal server error', details?: unknown, originalError?: unknown) {
    super(message, 500, details, originalError);
    this.name = 'InternalServerError';
    Object.setPrototypeOf(this, InternalServerError.prototype);
  }
}

/**
 * Bad Gateway Error (502)
 */
export class BadGatewayError extends ServerError {
  constructor(message: string = 'Bad gateway', details?: unknown, originalError?: unknown) {
    super(message, 502, details, originalError);
    this.name = 'BadGatewayError';
    Object.setPrototypeOf(this, BadGatewayError.prototype);
  }
}

/**
 * Service Unavailable Error (503)
 */
export class ServiceUnavailableError extends ServerError {
  constructor(message: string = 'Service unavailable', details?: unknown, originalError?: unknown) {
    super(message, 503, details, originalError);
    this.name = 'ServiceUnavailableError';
    Object.setPrototypeOf(this, ServiceUnavailableError.prototype);
  }
}

